<template>
  <div class="obs-program">
    <video
      v-show="hasVideo"
      ref="video"
      class="obs-program__video"
      autoplay
      playsinline
      muted
    />
    <div v-if="!hasVideo" class="obs-program__placeholder">
      <i class="fas fa-video" />
      <p v-if="!nodeId">Set device in widget settings</p>
      <p v-else-if="state === 'connecting'"><i class="fas fa-spinner fa-spin" /> Connecting…</p>
      <p v-else-if="state === 'error'">{{ error || 'Stream unavailable' }}</p>
      <p v-else>Waiting for OBS Virtual Camera…</p>
      <button
        v-if="nodeId && state === 'error'"
        type="button"
        class="obs-program__privacy-btn"
        :disabled="openingPrivacy"
        @click="openStationCameraPrivacy"
      >
        <i class="fas" :class="openingPrivacy ? 'fa-spinner fa-spin' : 'fa-cog'" />
        {{ openingPrivacy ? 'Membuka…' : 'Buka Camera Privacy (PC station)' }}
      </button>
      <p v-if="nodeId && state !== 'connecting'" class="obs-program__hint small">
        OBS → Start Virtual Camera. Aktifkan kamera untuk ControlLabs Connector di Windows Settings.
      </p>
    </div>
    <div v-if="hasVideo" class="obs-program__badge">
      <span class="obs-program__dot" />
      PROGRAM
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { OBSWebRTCSession, newSessionId, fetchIceServers } from '@/utils/obsWebRTC'

export default {
  props: {
    config: { type: Object, default: () => ({}) },
    widgetId: { type: String, default: '' },
  },
  data() {
    return {
      state: 'idle',
      error: '',
      session: null,
      hubListener: null,
      hasVideo: false,
      openingPrivacy: false,
    }
  },
  computed: {
    nodeId() {
      return this.config.nodeId || ''
    },
    deviceName() {
      return this.config.deviceName || 'OBS Virtual Camera'
    },
    sessionKey() {
      return `${this.nodeId}:${this.deviceName}`
    },
  },
  watch: {
    sessionKey: {
      immediate: true,
      handler() {
        this.restartSession()
      },
    },
  },
  beforeDestroy() {
    this.teardownSession()
    if (this.hubListener) {
      this.unregisterWebRTCListener(this.hubListener)
      this.hubListener = null
    }
  },
  methods: {
    ...mapActions('mission', ['sendCommand', 'sendWebRTCSignal', 'registerWebRTCListener', 'unregisterWebRTCListener']),
    teardownSession() {
      if (this.session) {
        this.session.stop()
        this.session = null
      }
      this.hasVideo = false
      this.state = 'idle'
      this.error = ''
      const video = this.$refs.video
      if (video) video.srcObject = null
    },
    async restartSession() {
      this.teardownSession()
      if (!this.nodeId) return

      if (!this.hubListener) {
        this.hubListener = (msg) => {
          if (this.session) this.session.handleHubMessage(msg)
        }
        this.registerWebRTCListener(this.hubListener)
      }

      const sessionId = newSessionId()
      let iceServers
      try {
        iceServers = await fetchIceServers()
      } catch (e) {
        iceServers = undefined
      }

      this.session = new OBSWebRTCSession({
        nodeId: this.nodeId,
        sessionId,
        deviceName: this.deviceName,
        iceServers,
        sendSignal: ({ action, payload }) => {
          this.sendWebRTCSignal({
            nodeId: this.nodeId,
            sessionId,
            action,
            payload,
          })
        },
        onState: (state, extra = {}) => {
          this.state = state
          if (state === 'error') {
            this.error = extra.message || 'Connection failed'
            this.hasVideo = false
            return
          }
          if (extra.stream) {
            this.attachStream(extra.stream)
          }
        },
      })
      try {
        await this.session.start()
      } catch (e) {
        this.state = 'error'
        this.error = e.message || 'Failed to start'
      }
    },
    attachStream(stream) {
      this.hasVideo = true
      this.$nextTick(() => {
        const video = this.$refs.video
        if (video) {
          video.srcObject = stream
          video.play().catch(() => {})
        }
      })
    },
    async openStationCameraPrivacy() {
      if (!this.nodeId || this.openingPrivacy) return
      this.openingPrivacy = true
      try {
        await this.sendCommand({
          node_id: this.nodeId,
          adapter: 'system',
          action: 'openCameraPrivacy',
        })
      } catch (e) {
        this.error = (e && e.message) || 'Gagal membuka pengaturan kamera di PC station'
      } finally {
        this.openingPrivacy = false
      }
    },
  },
}
</script>

<style scoped>
.obs-program {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 0;
  background: #000;
  overflow: hidden;
}
.obs-program__video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #000;
}
.obs-program__placeholder {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  padding: 0.75rem;
  text-align: center;
  color: var(--ws-text-muted, #9aa0a6);
  font-size: 0.78em;
}
.obs-program__placeholder i.fa-video {
  font-size: 1.4em;
  opacity: 0.55;
}
.obs-program__hint {
  opacity: 0.75;
  margin: 0;
}
.obs-program__privacy-btn {
  margin-top: 0.35rem;
  padding: 0.35em 0.65em;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.08);
  color: inherit;
  font-size: 0.72em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35em;
}
.obs-program__privacy-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
}
.obs-program__privacy-btn:disabled {
  opacity: 0.65;
  cursor: default;
}
.obs-program__badge {
  position: absolute;
  top: 0.35rem;
  left: 0.35rem;
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.15em 0.45em;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.55);
  color: #ff8e8e;
  font-size: 0.62em;
  font-weight: 800;
  letter-spacing: 0.06em;
}
.obs-program__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ef6e6e;
  box-shadow: 0 0 6px rgba(239, 110, 110, 0.8);
}
</style>
